import { groupBy } from 'lodash';
import Task from '../../../types/Task';
import { TaskStatus, TaskStatusGroup } from '../../../types/TaskStatus';
import { User } from '../../../types/User';
import { PriorityOptions } from '../../TaskTable/helpers/helper';
import { TaskTableDataType } from '../../TaskTable/types/TaskTableTypes';
import { WorkspaceViewSortDirection } from '../../../types/WorkspaceViewSortBy';
import { getPriorityIndex } from '../../TaskTable/helpers/helper';
import { Column } from '../../../types/WorkspaceViewColumn';
import { WorkspaceEstimationType } from '../../../types/Workspace';
import { TaskType } from '../../../types/TaskType';
import { getEffectiveStage } from '../../../utils/taskListUtils';

export const SortableFields = ['title', 'sprintId', 'assignee', 'statusId', 'deadline', 'priority'] as const;
export type SortableFieldType = (typeof SortableFields)[number];

export function sortComparatorFactory<ValueType extends TaskTableDataType | Task>(
  sortBy: SortableFieldType,
  sortOrder: 'asc' | 'desc',
  usersMap?: { [x: string]: User },
  priorityOrder?: PriorityOptions[],
  statusOrder?: number[]
): (a: ValueType, b: ValueType) => number {
  return (a, b) => {
    const defaultVal = sortOrder == 'asc' ? 'zzzz' : '0';
    const oppositeDefaultVal = sortOrder == 'desc' ? 'zzzz' : '0';

    let aVal = typeof a[sortBy] === 'string' ? a[sortBy].toLowerCase() : a[sortBy] || defaultVal;
    let bVal = typeof b[sortBy] === 'string' ? b[sortBy].toLowerCase() : b[sortBy] || defaultVal;

    switch (sortBy) {
      case 'priority':
        if (!priorityOrder) {
          throw new Error('Priority order is required for sorting by priority');
        }
        aVal = priorityOrder.indexOf(a[sortBy]);
        bVal = priorityOrder.indexOf(b[sortBy]);
        break;
      case 'sprintId':
        aVal = a.sprintId ? 2 : a.planningSessionId ? 1 : 0;
        bVal = b.sprintId ? 2 : b.planningSessionId ? 1 : 0;
        break;
      case 'statusId':
        if (!statusOrder) {
          throw new Error('Status order is required for sorting by status');
        }
        aVal = -statusOrder.indexOf(a[sortBy]);
        bVal = -statusOrder.indexOf(b[sortBy]);
        break;
      case 'deadline':
        if (aVal !== defaultVal && bVal !== defaultVal) {
          const temp = aVal;
          aVal = bVal;
          bVal = temp;
        }

        if (aVal instanceof Date) {
          aVal = aVal.toISOString();
        }
        if (bVal instanceof Date) {
          bVal = bVal.toISOString();
        }
        break;
      case 'assignee':
        if (!usersMap) {
          throw new Error('Users map is required for sorting by assignee');
        }
        bVal =
          usersMap && a[sortBy]
            ? (usersMap[a[sortBy]].fullName || usersMap[a[sortBy]].email).toLowerCase()
            : oppositeDefaultVal;
        aVal =
          usersMap && b[sortBy]
            ? (usersMap[b[sortBy]].fullName || usersMap[b[sortBy]].email).toLowerCase()
            : oppositeDefaultVal;
        break;
    }

    if (aVal > bVal) {
      return sortOrder == 'asc' ? 1 : -1;
    }
    if (aVal < bVal) {
      return sortOrder == 'asc' ? -1 : 1;
    }
    return 0;
  };
}

export function sortStatusOptions(options: TaskStatus[]): TaskStatus[] {
  const groupedOptions = groupBy(options, 'group');
  const groupOrder = [TaskStatusGroup.notStarted, TaskStatusGroup.inProgress, TaskStatusGroup.completed];
  const sortedGroupedOptions = groupOrder.map((group) =>
    (groupedOptions[group] || []).sort((a, b) => (a.orderIndex ?? a.id) - (b.orderIndex ?? b.id))
  );

  return sortedGroupedOptions.reduce((acc, curr) => [...acc, ...curr], []);
}

// Helper function to get a numeric value for TaskType sorting
const getTaskTypeOrderValue = (type: string | undefined): number => {
  if (!type) return Number.MAX_SAFE_INTEGER; // Undefined types go to the end

  const taskTypeOrder = {
    [TaskType.story]: 1,
    [TaskType.feature]: 2,
    [TaskType.improvement]: 3,
    [TaskType.experiment]: 4,
    [TaskType.task]: 5,
    [TaskType.bug]: 6,
    [TaskType.idea]: 7,
  };

  return taskTypeOrder[type as TaskType] || 8; // Unknown types go after known ones
};

export const sortGroupTasks = (
  groupTasks: TaskTableDataType[],
  tasksOrderMap: Map<number, number>,
  sortByColumn?: string,
  sortDirection?: WorkspaceViewSortDirection,
  activeSprintId?: string,
  planningSessionId?: string,
  estimationType?: WorkspaceEstimationType,
  statusesOrder?: number[],
  usersMap?: Map<number, User>
): TaskTableDataType[] => {
  const groupTasksWithSortedSubtasks = groupTasks.map((task) => {
    if (task.subtasks) {
      return {
        ...task,
        subtasks: sortTasks(
          task.subtasks,
          tasksOrderMap,
          sortByColumn,
          sortDirection,
          activeSprintId,
          planningSessionId,
          estimationType,
          statusesOrder,
          usersMap
        ),
      };
    }
    return task;
  });
  return sortTasks(
    groupTasksWithSortedSubtasks,
    tasksOrderMap,
    sortByColumn,
    sortDirection,
    activeSprintId,
    planningSessionId,
    estimationType,
    statusesOrder,
    usersMap
  );
};

const sortTasks = (
  groupTasks: Task[],
  tasksOrderMap: Map<number, number>,
  sortByColumn?: string,
  sortDirection?: WorkspaceViewSortDirection,
  activeSprintId?: string,
  planningSessionId?: string,
  estimationType?: WorkspaceEstimationType,
  statusesOrder?: number[],
  usersMap?: Map<number, User>
): TaskTableDataType[] => {
  return groupTasks.sort((a: Task, b: Task) => {
    if (sortByColumn) {
      let sortValue = 0;
      const defaultValue =
        sortDirection === WorkspaceViewSortDirection.asc ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      switch (sortByColumn) {
        case Column.taskId:
          sortValue = b.id - a.id;
          break;
        case Column.taskType:
          // Use the new helper function for TaskType sorting
          sortValue = getTaskTypeOrderValue(b.type) - getTaskTypeOrderValue(a.type);
          break;
        case Column.assignee:
          if (!a.assignee && !b.assignee) {
            sortValue = 0;
          } else if (!a.assignee) {
            return 1; // a goes to end
          } else if (!b.assignee) {
            return -1; // b goes to end
          } else {
            const userA = usersMap?.get(a.assignee);
            const userB = usersMap?.get(b.assignee);
            const nameA = (userA?.fullName || userA?.email || '').toLowerCase();
            const nameB = (userB?.fullName || userB?.email || '').toLowerCase();
            sortValue = nameB.localeCompare(nameA);
          }
          break;
        case Column.status:
          sortValue = (statusesOrder?.indexOf(b.statusId) ?? 0) - (statusesOrder?.indexOf(a.statusId) ?? 0);
          break;
        case Column.estimate:
          if (estimationType === WorkspaceEstimationType.tshirtSizes) {
            if (a.estimate && b.estimate) {
              sortValue = b.estimate - a.estimate;
            } else {
              sortValue = (a.estimate ?? defaultValue) - (b.estimate ?? defaultValue);
            }
          } else {
            if (a.storyPoints && b.storyPoints) {
              sortValue = b.storyPoints - a.storyPoints;
            } else {
              sortValue = (a.storyPoints ?? defaultValue) - (b.storyPoints ?? defaultValue);
            }
          }
          break;
        case Column.priority:
          if (a.priority === PriorityOptions.NoPriority && b.priority === PriorityOptions.NoPriority) {
            sortValue = 0;
          } else if (a.priority === PriorityOptions.NoPriority) {
            return 1; // a goes to end
          } else if (b.priority === PriorityOptions.NoPriority) {
            return -1; // b goes to end
          } else {
            sortValue = getPriorityIndex(a.priority) - getPriorityIndex(b.priority);
          }
          break;
        case Column.epic:
          if (!a.epicId && !b.epicId) {
            sortValue = 0;
          } else if (!a.epicId) {
            return 1; // a goes to end
          } else if (!b.epicId) {
            return -1; // b goes to end
          } else {
            sortValue = b.epicId - a.epicId;
          }
          break;
        case Column.startDate:
          if (!a.startDate && !b.startDate) {
            sortValue = 0;
          } else if (!a.startDate) {
            return 1; // a goes to end
          } else if (!b.startDate) {
            return -1; // b goes to end
          } else {
            const aDate = new Date(a.startDate);
            const bDate = new Date(b.startDate);
            sortValue = aDate.getTime() - bDate.getTime();
          }
          break;
        case Column.deadline:
          if (!a.deadline && !b.deadline) {
            sortValue = 0;
          } else if (!a.deadline) {
            return 1; // a goes to end
          } else if (!b.deadline) {
            return -1; // b goes to end
          } else {
            const aDate = new Date(a.deadline);
            const bDate = new Date(b.deadline);
            sortValue = aDate.getTime() - bDate.getTime();
          }
          break;
        case Column.sprintStatus:
          const statusOrder = { sprint: 1, planning: 2, none: 3 };
          const numericSprintId = activeSprintId ? Number(activeSprintId) : undefined;
          // Bucket by the SAME stage the Stage column renders. Reading sprintId/
          // planningSessionId here was a second source of truth: a task set to Planning
          // from the Stage dropdown gets `stage:'planning'` but no planningSessionId, so
          // it showed a Planning chip while sorting into the Backlog bucket. #2969
          const toStatus = (task: Task) => {
            const stage = getEffectiveStage(task, numericSprintId);
            return stage === 'sprint' || stage === 'planning' ? stage : 'none';
          };
          const aStatus = toStatus(a);
          const bStatus = toStatus(b);
          if (aStatus === 'none' && bStatus === 'none') {
            sortValue = 0;
          } else if (aStatus === 'none') {
            return 1; // a goes to end
          } else if (bStatus === 'none') {
            return -1; // b goes to end
          }
          sortValue = statusOrder[aStatus] - statusOrder[bStatus];
          break;
        case Column.createdAt:
          sortValue = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case Column.updatedAt:
          sortValue = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }
      if (sortValue !== 0) {
        return sortDirection === WorkspaceViewSortDirection.desc ? -sortValue : sortValue;
      }
    }

    const aIndex = tasksOrderMap.get(a.id);
    const bIndex = tasksOrderMap.get(b.id);

    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }

    return a.id - b.id;
  });
};
